import { Divider } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IProcessQuestionnaireResponseItem } from '@common/interfaces/api/processQuestionnaireResponsesApi';
import type { IQuestionnaireItem } from '@common/interfaces/api/questionnairesApi';
import { QuestionnaireName } from '@common/enums';
import { getQuestionnaireResponseScore } from '@common/utils/osasQuestionnaireItemsMappingUtils';
import { QuestionnaireHeaderScore } from '@frontend/components/elements/questionnaireResponse/QuestionnaireHeaderScore';
import { getProcessQuestionnaireResponse } from '@frontend/services/api/processQuestionnaireResponsesApiService';
import { getQuestionnaireItems } from '@frontend/services/api/questionnairesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { BadgeQuestionnaireResponse } from './BadgeQuestionnaireResponse';
import { HsdqQuestionnaireResponse } from './HsdqQuestionnaireResponse';

interface IQuestionnaireResponseProps {
    processQuestionnaireResponseId: string;
    questionnaireName: QuestionnaireName;
}

export function QuestionnaireResponse(props: Readonly<IQuestionnaireResponseProps>) {
    const { processQuestionnaireResponseId, questionnaireName } = props;
    const abortSignal = useAbortSignal();
    const [questionnaireId, setQuestionnaireId] = useState<string>();
    const [questionnaireItems, setQuestionnaireItems] = useState<IQuestionnaireItem[]>([]);
    const [questionnaireResponseItems, setQuestionnaireResponseItems] = useState<IProcessQuestionnaireResponseItem[]>(
        []
    );

    useEffect(() => {
        if (questionnaireId) {
            loadQuestionnaireItems(questionnaireId);
        }
    }, [questionnaireId]);

    useEffect(() => {
        loadQuestionnaireResponseItems(processQuestionnaireResponseId);
    }, [processQuestionnaireResponseId]);

    return (
        <>
            <Divider />

            <QuestionnaireHeaderScore
                questionnaireName={questionnaireName}
                value={getQuestionnaireResponseScore(
                    questionnaireResponseItems,
                    questionnaireName === QuestionnaireName.osasHsdq
                )}
            />

            {questionnaireName === QuestionnaireName.osasHsdq && questionnaireResponseItems && (
                <HsdqQuestionnaireResponse
                    questionnaireItems={questionnaireItems}
                    questionnaireResponseItems={questionnaireResponseItems}
                />
            )}

            {questionnaireName === QuestionnaireName.osasEss && questionnaireResponseItems && (
                <BadgeQuestionnaireResponse questionnaireResponseItems={questionnaireResponseItems} />
            )}
        </>
    );

    async function loadQuestionnaireResponseItems(id: string) {
        const response = await getProcessQuestionnaireResponse(
            id,
            {
                includeItems: true,
            },
            abortSignal
        );

        setQuestionnaireId(response.questionnaireId);

        if (response.items) {
            setQuestionnaireResponseItems(response.items);
        }
    }

    async function loadQuestionnaireItems(id: string) {
        const { items } = await getQuestionnaireItems(
            id,
            {
                includeAnswerOptions: true,
            },
            abortSignal
        );

        if (items) {
            setQuestionnaireItems(items);
        }
    }
}
